import React, { useRef } from 'react';
import { useIntl } from '@olxpwa-lib/i18n/client';
import { Button, DayProps, useDayRender } from 'react-day-picker';
import {
    StyledGreenBgTooltip,
    StyledTooltip
} from '~components/AdServiceBooking/RenderCalendarDay';
import { format } from 'date-fns';

const MinimmumStayRequimentTooltip = ({
    mergedButtonProps,
    buttonRef,
    minimumStayRequirement
}: {
    mergedButtonProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
    buttonRef: React.RefObject<HTMLButtonElement>;
    minimumStayRequirement?: number;
}) => {
    const { formatMessage } = useIntl();
    return (
        <StyledGreenBgTooltip
            touch
            placement="top"
            content={formatMessage(
                {
                    id: 'ad.booking.minimumstay',
                    defaultMessage: 'Minimum number of nights: {nights}'
                },
                { nights: minimumStayRequirement ? minimumStayRequirement : 1 }
            )}
            trigger="mouseenter"
            verticalOffset={3}
            fontSize="small"
        >
            <Button
                {...mergedButtonProps}
                tabIndex={-1}
                role="button"
                ref={buttonRef}
                data-testid="minimumStayRequirementTooltip"
            />
        </StyledGreenBgTooltip>
    );
};

export const RenderCalendarDay = (
    {
        props,
        minimumStayRequirement
    }: { props: DayProps; minimumStayRequirement?: number },
    enableBlockDatesFeature = false
) => {
    const { formatMessage } = useIntl();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);
    const ariaLabelFormatters = format(props.date, 'do MMMM (EEEE)');
    const { activeModifiers } = dayRender;
    const mergedButtonProps = {
        ...dayRender.buttonProps,
        'aria-label': ariaLabelFormatters,
        'aria-pressed': dayRender.buttonProps['aria-selected']
    };

    const normalDay = !activeModifiers.checkOutDates; //&& !activeModifiers.checkInDates; ===> to add checkin dates later!

    const checkOutOnlyText = formatMessage({
        id: 'ad.booking.checkout.tooltip',
        defaultMessage: 'Checkout only'
    });

    const checkInOnlyText = formatMessage({
        id: 'ad.booking.checkin.tooltip',
        defaultMessage: 'Checkin only'
    });

    const renderCustomDay = () => {
        return (
            <StyledTooltip
                touch
                placement="top"
                content={
                    activeModifiers.checkOutDates
                        ? checkOutOnlyText
                        : checkInOnlyText
                }
                trigger="mouseenter"
                verticalOffset={3}
            >
                <Button
                    data-testid="customDay"
                    {...mergedButtonProps}
                    role="button"
                    ref={buttonRef}
                />
            </StyledTooltip>
        );
    };

    const isDateInTheMinimumStayRequirement =
        activeModifiers.minimumStayRequirementDates &&
        minimumStayRequirement &&
        minimumStayRequirement > 1;

    if (isDateInTheMinimumStayRequirement) {
        return (
            <MinimmumStayRequimentTooltip
                mergedButtonProps={mergedButtonProps}
                buttonRef={buttonRef}
                minimumStayRequirement={minimumStayRequirement}
            />
        );
    }
    if (!normalDay && !enableBlockDatesFeature) {
        return renderCustomDay();
    }

    return (
        <Button
            {...mergedButtonProps}
            tabIndex={-1}
            role="button"
            ref={buttonRef}
        />
    );
};
